import { type CSSProperties, useEffect, useState } from "react";

import { useUi } from "../state/ui";

/** Apparition des annotations de la carte (couches du camp, ponts, fuseau, LIMA, unités, symboles de mission) :
 *  la carte reste vide tant que le dossier n'est pas créé ; à sa création, ou à l'ouverture d'un dossier, elles
 *  apparaissent en fondu (demande d'Alexis du 28/09/2026). Les couches restent montées, transparentes : leurs
 *  données sont déjà chargées quand le fondu commence. */

export const REVEAL_MS = 900;

/** Propriétés d'opacité animées, par type de couche. */
const OPACITY: Record<string, string[]> = {
  fill: ["fill-opacity"],
  line: ["line-opacity"],
  circle: ["circle-opacity", "circle-stroke-opacity"],
  symbol: ["icon-opacity", "text-opacity"],
};

/** Annotations visibles : un dossier est créé ou ouvert. */
export function useMapRevealed(): boolean {
  return useUi((s) => s.exerciseId !== null);
}

/** Peinture d'une couche d'annotations : opacité nulle avant la création du dossier, puis fondu jusqu'à son
 *  opacité propre (transition du moteur de carte). */
export function revealPaint(
  type: string,
  paint: Record<string, unknown> | undefined,
  shown: boolean,
  duration = REVEAL_MS,
): Record<string, unknown> {
  const out: Record<string, unknown> = { ...(paint ?? {}) };
  for (const key of OPACITY[type] ?? []) {
    const base = out[key] ?? 1;
    out[key] = shown ? base : 0;
    out[`${key}-transition`] = { duration, delay: 0 };
  }
  return out;
}

/** Même fondu pour les repères HTML posés sur la carte (étiquettes des lignes). */
export function revealStyle(shown: boolean, duration = REVEAL_MS): CSSProperties {
  return { opacity: shown ? 1 : 0, transition: `opacity ${duration}ms ease` };
}

/** Fondu de nouvelles données sur la carte (`key` : leur identité, un nouveau rapport par exemple) : faux au
 *  premier rendu — les couches sont créées transparentes —, vrai un instant après, et le moteur de carte fait
 *  monter l'opacité (règle d'Alexis du 29/09/2026 : rien n'apparaît d'un coup). */
export function useFadeIn(key: unknown): boolean {
  const [shownKey, setShownKey] = useState<unknown>(undefined);
  useEffect(() => {
    const timer = window.setTimeout(() => setShownKey(key), 40);
    return () => window.clearTimeout(timer);
  }, [key]);
  return key !== null && key !== undefined && shownKey === key;
}
